import { useMemo, useSyncExternalStore } from 'react';
import { useSpeakerDetailsForBroadcast } from '@/App/remotion/MainPlayer/stores/speakerDetailsStore';

import { alignedRecordings, getAlignedRecordings } from '@/App/remotion/MainPlayer/stores/alignedRecordings';
import { isEntityUserFeed } from '@/libs/activityUtils';
import { getClipBroadcastId } from '@/libs/clipUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { isRecordingScreenshare, useVideoDetails } from '@/App/remotion/MainPlayer/stores/videoSpeakerDetailsStore';
import { isScreenShareEnabledForCustomUploadClips } from '@/stores/clip';
import { speakersAnalysisStore } from '@/stores/speakersAnalysis/speakersAnalysis';

export const useTimelineSpeakers = () => {
  const { clipData } = useClipsContext();
  const alignedRecordingsStore = useSyncExternalStore(alignedRecordings.subscribe, alignedRecordings.getSnapshot);
  const speakersAnalysisForAllClips = useSyncExternalStore(
    speakersAnalysisStore.subscribe,
    speakersAnalysisStore.getSnapshot
  );
  const broadcastId = getClipBroadcastId(clipData)!;
  const recordings = useMemo(
    () => getAlignedRecordings(broadcastId, alignedRecordingsStore),
    [broadcastId, alignedRecordingsStore]
  );
  const speakerDetails = useSpeakerDetailsForBroadcast(broadcastId);
  const videoSpeakerDetails = useVideoDetails();

  const speakers = useMemo(() => {
    const nonUserSpeakerIds = Object.values(recordings)
      // TODO: @@HiteshChoudhary0098 this needs to be revisited. What backend stopped sending types for transcript mapping.
      // @ts-ignore
      .filter(recording => !isEntityUserFeed(recording.entity) && recording.transcript_mapping?.speaker_labels[0])
      // @ts-ignore
      .map(recording => recording.transcript_mapping?.speaker_labels[0]);

    return Object.values(speakerDetails)
      .filter(speaker => !speaker.parent_id)
      .filter(speaker => !nonUserSpeakerIds.includes(speaker.key));
  }, [speakerDetails, recordings]);

  const screenshareExists = useMemo(() => {
    const hasRecordingScreenshare = recordings.some(recording =>
      isRecordingScreenshare({ broadcastId, recordingId: recording.id, snapshot: videoSpeakerDetails })
    );

    const shouldCheckCustomUploads = isScreenShareEnabledForCustomUploadClips(clipData);

    if (shouldCheckCustomUploads) {
      const speakersAnalysis = speakersAnalysisForAllClips[clipData.id];
      return (
        hasRecordingScreenshare ||
        (speakersAnalysis?.screenshare_bounds && speakersAnalysis.screenshare_bounds.length > 0)
      );
    }

    return hasRecordingScreenshare;
  }, [recordings, videoSpeakerDetails, broadcastId, clipData, speakersAnalysisForAllClips]);

  return {
    speakers,
    recordings,
    screenshareExists
  };
};
